import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import { api } from '../services/api';

interface ReschedulePaymentProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  saleId: number;
  installmentNumber: number;
  customerName: string;
  currentDueDate: Date;
  remainingBalance?: number;
}

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);

const ReschedulePayment: React.FC<ReschedulePaymentProps> = ({
  open,
  onClose,
  onSuccess,
  saleId,
  installmentNumber,
  customerName,
  currentDueDate,
  remainingBalance,
}) => {
  const [newDueDate, setNewDueDate] = useState<Date | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!newDueDate) {
      setError('Por favor selecciona una nueva fecha');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await api.post('/payments/reschedule', {
        sale_id: saleId,
        installment_number: installmentNumber,
        new_due_date: format(newDueDate, 'yyyy-MM-dd'),
        reason: reason || null
      });

      onSuccess();
      onClose();
      
      // Reset form
      setNewDueDate(null);
      setReason('');
    } catch (error) {
      console.error('Error rescheduling payment:', error);
      setError('Error al reprogramar el pago. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setNewDueDate(null);
      setReason('');
      setError(null);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Reprogramar Pago
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Cliente: <strong>{customerName}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Cuota: <strong>#{installmentNumber}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fecha actual: <strong>{format(currentDueDate, 'dd/MM/yyyy')}</strong>
            </Typography>
            {remainingBalance != null && remainingBalance > 0 && (
              <Typography variant="body2" sx={{ color: '#3B82F6', fontWeight: 600, mt: 0.5 }}>
                Saldo pendiente: {fmtCurrency(remainingBalance)}
              </Typography>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <DatePicker
              label="Nueva Fecha de Cobro"
              value={newDueDate}
              onChange={(date) => setNewDueDate(date)}
              slots={{
                textField: TextField
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small"
                }
              }}
            />
          </Box>

          <TextField
            label="Motivo (opcional)"
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            size="small"
            placeholder="Ej: Cliente no tiene dinero disponible, reprogramar para el viernes..."
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={loading || !newDueDate}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
              }
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Reprogramar'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ReschedulePayment;
